import { difference, filter, intersection, isEmpty, orderBy } from "lodash";
import { type useLocation } from "react-router-dom";
import { TabProps } from "@services/api/tab/tab.types";
import { getFirstFilter } from "@src/app/filters/Filters";
import { type TabMode, TabModeEnum } from "../types/TabMode";
import { getTabUrl } from "./tabUrlUtils";

type ScoredTab = {
  tab: TabProps;
  isMainTab: boolean;
  isTypeInPath: boolean;
  matchingFiltersScore: number;
  sequence: number;
};

export function getActiveMainTab(
  sortedTabs: TabProps[],
  location: ReturnType<typeof useLocation>
): TabProps | undefined {
  const { search } = location;
  const searchParams = new URLSearchParams(search);

  const scoredTabs: ScoredTab[] = sortedTabs.map((tab) => ({
    tab,
    isMainTab: isMainTabIncludedInPath(searchParams, tab),
    isTypeInPath: isTabUrlIncludedInPath(tab),
    matchingFiltersScore: getMatchingFiltersScore(tab, searchParams),
    sequence: tab.sequence
  }));

  const validTabs = filter(scoredTabs, { isTypeInPath: true });

  return getBestTab(validTabs);
}

function getMatchingFiltersScore(
  tab: TabProps,
  searchParams: URLSearchParams
): number {
  const { key: filterKey, values: filterValues } = getFirstFilter(tab.filter);
  const searchValues = searchParams.getAll(filterKey);

  const noSearchQueryForKey = searchValues.length === 0;
  if (noSearchQueryForKey) {
    return 0; // Determine tab based on the sequence only
  }

  const extraCount = difference(searchValues, filterValues).length;
  const matchCount = intersection(searchValues, filterValues).length;
  const missingCount = filterValues.length - matchCount;

  const isExactMatch = missingCount === 0 && extraCount === 0;
  if (isExactMatch) {
    return Number.POSITIVE_INFINITY;
  }

  return matchCount - extraCount; // Higher score for more matches, lower for extra values
}

function getBestTab(tabs: ScoredTab[]): TabProps | undefined {
  const iteratees: Array<keyof ScoredTab> = [
    "isMainTab",
    "matchingFiltersScore",
    "sequence"
  ];
  const sortedTabs = orderBy(tabs, iteratees, ["desc", "desc", "asc"]);

  const bestTab = sortedTabs[0];
  return bestTab ? bestTab.tab : undefined;
}

// Needed for backwards compatibility and single-faculty scenarios
function isMainTabIncludedInPath(searchParams: URLSearchParams, tab: TabProps) {
  const mainTab = searchParams.get("mainTab");
  return mainTab === tab.name;
}

function isTabUrlIncludedInPath(tab: TabProps): boolean {
  if (!tab) {
    return false;
  }

  const currentPath = window.location.pathname;
  const url = getTabUrl(tab);
  if (!url) {
    return false;
  }

  return !isEmpty(url) && currentPath.includes(url);
}

export function getTabModes(modes: string | undefined): TabMode[] {
  // No mode suggests a new tab, built with WidgetType.PRIMARY
  if (!modes) {
    return [TabModeEnum.LIST, TabModeEnum.TABLE];
  }

  return modes.split(",") as TabMode[];
}

export function tabIsParent(tab: TabProps): boolean {
  if (tab.parentTabs === undefined) {
    return false;
  }

  return !tab.parentTabs.length;
}
